import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx } from "react/jsx-runtime";
import { Platform, PlatformNoHandlers, useQuery } from '@core/api';
import { sanitizeOrganizationId } from '@core/configuration';
import Registry from '@core/registry';
import { createContext, useContext, useLayoutEffect, useState } from 'react';
import { useMatch, useNavigate } from 'react-router';
import { OrganizationLocalStorage } from '../OrganizationLocalStorage.js';
export var OrganizationContext = /*#__PURE__*/ createContext({
    organizationModelQuery: undefined,
    organizationDefinitionQuery: undefined
});
export var useOrganization = function useOrganization() {
    var context = useContext(OrganizationContext);
    if (!context || !context.organizationModelQuery) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    if (!context.organizationModelQuery.data) {
        throw new Error('Organization data is not available');
    }
    return context.organizationModelQuery.data;
};
export var useOrganizationDefinition = function useOrganizationDefinition() {
    var context = useContext(OrganizationContext);
    if (!context || !context.organizationDefinitionQuery) {
        throw new Error('useOrganizationDefinition must be used within an OrganizationProvider');
    }
    if (!context.organizationDefinitionQuery.data) {
        throw new Error('Organization definition data is not available');
    }
    return context.organizationDefinitionQuery.data;
};
/**
 * Fetch the organization model for the given organization ID.
 * @param currentOrg The ID of the organization to fetch.
 * @returns A promise that resolves to a tuple containing the organization model and a boolean indicating if the first org fallback was used.
 */ var fetchOrganizationModel = function fetchOrganizationModel(currentOrg) {
    return _async_to_generator(function() {
        var organization, unused, _ref, _list_items, list;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    _state.trys.push([
                        0,
                        2,
                        ,
                        4
                    ]);
                    if (!currentOrg || currentOrg === 'unable' || currentOrg === 'invites') {
                        throw new Error('Not a real organization, pick the first org instead');
                    }
                    return [
                        4,
                        PlatformNoHandlers.organization.get(currentOrg, {
                            additionalFields: 'status'
                        })
                    ];
                case 1:
                    organization = _state.sent();
                    return [
                        2,
                        [
                            organization,
                            false
                        ]
                    ];
                case 2:
                    unused = _state.sent();
                    return [
                        4,
                        Platform.organization.list({
                            page: 0,
                            perPage: 1,
                            sortBy: 'displayName'
                        })
                    ];
                case 3:
                    list = _state.sent();
                    return [
                        2,
                        [
                            (_ref = (_list_items = list.items) === null || _list_items === void 0 ? void 0 : _list_items[0]) !== null && _ref !== void 0 ? _ref : null,
                            true
                        ]
                    ];
                case 4:
                    return [
                        2
                    ];
            }
        });
    })();
};
/**
 * Provides the organization context to its children.
 *
 * Constraint:
 * * has to be within context of the QueryClientProvider.
 */ export var OrganizationProvider = function OrganizationProvider(param) {
    var children = param.children, ignoreOrgChangeInUrl = param.ignoreOrgChangeInUrl;
    var _ref;
    var _useMatch_params, _useMatch, _organizationModelQuery_data;
    var navigate = useNavigate();
    var organizationIdFromUrl = sanitizeOrganizationId((_ref = (_useMatch = useMatch('/:orgId/*')) === null || _useMatch === void 0 ? void 0 : (_useMatch_params = _useMatch.params) === null || _useMatch_params === void 0 ? void 0 : _useMatch_params.orgId) !== null && _ref !== void 0 ? _ref : '');
    var currentOrg = organizationIdFromUrl || OrganizationLocalStorage.getOrganizationFromLocalStorage() || null;
    var _useState = _sliced_to_array(useState(false), 2), isUsingFirstOrg = _useState[0], setIsUsingFirstOrg = _useState[1];
    var organizationModelQuery = useQuery({
        queryKey: [
            'init',
            'organization',
            currentOrg
        ],
        queryFn: function queryFn() {
            return _async_to_generator(function() {
                var _ref, _ref1, _ref2, organization, hasUsedFirstOrgFallback;
                return _ts_generator(this, function(_state) {
                    switch(_state.label){
                        case 0:
                            return [
                                4,
                                fetchOrganizationModel(currentOrg)
                            ];
                        case 1:
                            _ref2 = _sliced_to_array.apply(void 0, [
                                _state.sent(),
                                2
                            ]), organization = _ref2[0], hasUsedFirstOrgFallback = _ref2[1];
                            OrganizationLocalStorage.setOrganizationInLocalStorage((_ref = organization === null || organization === void 0 ? void 0 : organization.id) !== null && _ref !== void 0 ? _ref : '');
                            Registry.register('organization_id', (_ref1 = organization === null || organization === void 0 ? void 0 : organization.id) !== null && _ref1 !== void 0 ? _ref1 : '');
                            Registry.register('organization', organization);
                            setIsUsingFirstOrg(hasUsedFirstOrgFallback);
                            if (hasUsedFirstOrgFallback && organization) {
                                navigate("/".concat(organization.id), {
                                    replace: true
                                });
                            }
                            return [
                                2,
                                organization
                            ];
                    }
                });
            })();
        },
        retry: false,
        initialData: Registry.get('organization', {
            optional: true
        }),
        staleTime: Infinity
    });
    var organizationDefinitionQuery = useQuery({
        queryKey: [
            'init',
            'organization',
            'definition'
        ],
        queryFn: function queryFn() {
            return Platform.organization.getDefinition();
        },
        retry: false,
        initialData: Registry.get('organization_definition', {
            optional: true
        }),
        staleTime: Infinity,
        enabled: !!organizationModelQuery.data
    });
    useLayoutEffect(function() {
        var _organizationModelQuery_data, _organizationModelQuery_data1;
        if (!ignoreOrgChangeInUrl && (organizationModelQuery === null || organizationModelQuery === void 0 ? void 0 : (_organizationModelQuery_data = organizationModelQuery.data) === null || _organizationModelQuery_data === void 0 ? void 0 : _organizationModelQuery_data.id) && organizationIdFromUrl && !isUsingFirstOrg && organizationIdFromUrl !== (organizationModelQuery === null || organizationModelQuery === void 0 ? void 0 : (_organizationModelQuery_data1 = organizationModelQuery.data) === null || _organizationModelQuery_data1 === void 0 ? void 0 : _organizationModelQuery_data1.id)) {
            navigate(0); // reload page
        }
    }, [
        ignoreOrgChangeInUrl,
        organizationIdFromUrl,
        organizationModelQuery === null || organizationModelQuery === void 0 ? void 0 : (_organizationModelQuery_data = organizationModelQuery.data) === null || _organizationModelQuery_data === void 0 ? void 0 : _organizationModelQuery_data.id,
        isUsingFirstOrg
    ]);
    return /*#__PURE__*/ _jsx(OrganizationContext.Provider, {
        value: {
            organizationModelQuery: organizationModelQuery,
            organizationDefinitionQuery: organizationDefinitionQuery
        },
        children: children
    });
};

//# sourceMappingURL=OrganizationContext.js.map